import { type ComponentTFunction } from '@pkgs/components/providers/AppContextProvider';
import { type SearchListingPreferenceScores, type SearchRestrictedListingPreferenceScores } from '@ev-tech/search-modules-api';

import { type UIFacingPreferences } from '../hooks/stores/search/slices/preferencesSlice';
import {
  EXCELLENT_PREFERENCE_SCORE_THRESHOLD,
  GOOD_PREFERENCE_SCORE_THRESHOLD,
  INTERNAL_PREFERENCE_PREFIX,
  PREFERENCES_TRANSLATION_MAP,
  VERY_GOOD_PREFERENCE_SCORE_THRESHOLD,
} from '../consts/aiSearch';
import { type PreferencesObject, type PositivePreferenceMatch } from '../components/SearchResultCard/SearchResultCard.types';

export const getPreferenceTranslation = (preference: keyof UIFacingPreferences, t: ComponentTFunction): string =>
  t(PREFERENCES_TRANSLATION_MAP[preference]);

export const getSelectedPreferenceEntries = (
  preferences: UIFacingPreferences | undefined,
  t: ComponentTFunction,
): { preference: keyof UIFacingPreferences; label: string }[] =>
  (Object.entries(preferences ?? {}) as [keyof UIFacingPreferences, number | undefined][])
    .filter(([preference, value]) => Boolean(value) && !preference.startsWith(INTERNAL_PREFERENCE_PREFIX))
    .map(([preference]) => ({
      preference,
      label: getPreferenceTranslation(preference, t),
    }));

export const convertDecimalToPercentage = (preferenceScoreOverall?: number): string => {
  if (!preferenceScoreOverall) return '';

  return `${(preferenceScoreOverall * 100).toFixed(0)}%`;
};

export const convertScoreToLabel = (score: number, t: ComponentTFunction): string => {
  if (score >= EXCELLENT_PREFERENCE_SCORE_THRESHOLD) {
    return t('search.aiSearch.scoresDropdown.excellent');
  }

  if (score >= VERY_GOOD_PREFERENCE_SCORE_THRESHOLD) {
    return t('search.aiSearch.scoresDropdown.veryGood');
  }

  if (score >= GOOD_PREFERENCE_SCORE_THRESHOLD) {
    return t('search.aiSearch.scoresDropdown.good');
  }

  return '';
};

// Reason: Smart-sort EV_* factors are backend-only; never surface them in preference score UI.
export const getPositivePreferenceMatchScores = (
  preferenceScores: SearchListingPreferenceScores,
  t: ComponentTFunction,
): PositivePreferenceMatch[] =>
  Object.entries(preferenceScores)
    .filter(([key, value]) => !key.startsWith(INTERNAL_PREFERENCE_PREFIX) && value >= GOOD_PREFERENCE_SCORE_THRESHOLD)
    .sort(([, leftScore], [, rightScore]) => rightScore - leftScore)
    .map(([key, value]) => [key as keyof UIFacingPreferences, convertScoreToLabel(value, t), value]);

// Reason: Smart-sort EV_* factors are backend-only; never surface them in preference score UI.
export const getPoorPreferenceMatches = (preferenceScores?: SearchListingPreferenceScores): (keyof UIFacingPreferences)[] => {
  if (!preferenceScores) return [];

  return Object.entries(preferenceScores)
    .filter(([key, value]) => !key.startsWith(INTERNAL_PREFERENCE_PREFIX) && value < GOOD_PREFERENCE_SCORE_THRESHOLD)
    .map(([key]) => key as keyof UIFacingPreferences)
    .sort((leftKey, rightKey) => leftKey.localeCompare(rightKey));
};

export const getPreferencesObject = ({
  t,
  preferenceScores,
  preferenceScoreOverall,
}: {
  t: ComponentTFunction;
  preferenceScores: SearchRestrictedListingPreferenceScores;
  preferenceScoreOverall: number;
}): PreferencesObject => ({
  overallLabel: convertScoreToLabel(preferenceScoreOverall, t),
  overallScore: preferenceScoreOverall * 100,
  overallPercentage: convertDecimalToPercentage(preferenceScoreOverall),
  matchingPositivePreferenceMatchs: getPositivePreferenceMatchScores(preferenceScores, t),
  matchingPoorPreferences: getPoorPreferenceMatches(preferenceScores),
});
